import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { lessonPlanAPI, schoolAPI, aiAPI, documentAPI, voiceAPI } from '@/services/api';
import CurriculumPicker from '@/components/CurriculumPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Save, Download, Sparkles, Wand2, ExternalLink, Home, History, Share2, RotateCcw, Copy, Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/services/i18n';
import { templateAPI } from '@/services/api';

const LessonPlanBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, setLang } = useI18n();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [school, setSchool] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    subject: '',
    class_name: '',
    date_range: '',
    num_days: 5,
    board: '',
    book_name: '',
    language: user?.preferred_language || 'en',
    template_id: user?.default_template_id || '',
    chapter: '',
    teacher_name: user?.name || '',
    custom_school_name: '',
    days: [{ day: 'DAY 1', outcomes: '', curriculum_goals: '', teacher_activity: '', student_activity: '', skills: '', innovative_practices: '', exit_card: '', remarks: '' }],
    integration: '',
    interdisciplinary: '',
    innovative: '',
    assessments: '',
    is_draft: true,
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [autoGenLoading, setAutoGenLoading] = useState(false);
  const [webReferences, setWebReferences] = useState([]);
  const [autoTriggered, setAutoTriggered] = useState(false);
  const [libScope, setLibScope] = useState({ book_ids: [], chapter_ids: [], topic_ids: [], scope_summary: '', filters: {} });
  const [aiProvider, setAiProvider] = useState('openai');
  const autoTriggerTimer = useRef(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [orientation, setOrientation] = useState('landscape');
  const [selectedAuthorities, setSelectedAuthorities] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [creatingShareLink, setCreatingShareLink] = useState(false);
  const SIGNING_AUTHORITIES = ['Subject Teacher', 'Head of Department', 'Coordinator', 'Academic Coordinator', 'Vice Principal', 'Principal'];

  // Curriculum Pack — chapter suggestions come from the CurriculumPicker (Library) only

  // Voice Compose
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [voiceState, setVoiceState] = useState('idle'); // idle | recording | processing | done
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceIntent, setVoiceIntent] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    if (user?.school_id) {
      loadSchool();
    }
    if (id) {
      loadLessonPlan();
    }
    (async () => {
      try {
        const { data } = await templateAPI.getAll();
        setTemplates(data || []);
      } catch { /* templates optional */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.school_id]);

  // Fetch curriculum chapters from Library-driven CurriculumPicker only
  useEffect(() => {
    if (!libScope) return;
    // If exactly one chapter is picked, populate the single `chapter` field from it
    const f = libScope.filters || {};
    const patch = {};
    if (f.class_name && !formData.class_name) patch.class_name = f.class_name;
    if (f.subject && !formData.subject) patch.subject = f.subject;
    if (f.board && !formData.board) patch.board = f.board;
    if (libScope.chapter_ids?.length === 1 && libScope.scope_summary && !formData.chapter) {
      patch.chapter = libScope.scope_summary.split('·').slice(1).join('·').trim() || libScope.scope_summary;
    }
    if (Object.keys(patch).length) setFormData((p) => ({ ...p, ...patch }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libScope]);

  const loadSchool = useCallback(async () => {
    try {
      const { data } = await schoolAPI.get(user.school_id);
      setSchool(data);
    } catch (error) {
      console.error('Failed to load school:', error);
    }
  }, [user?.school_id]);

  const loadLessonPlan = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await lessonPlanAPI.getOne(id);
      setFormData(data);
    } catch (error) {
      toast.error('Failed to load lesson plan');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const handleSave = async (isDraft = true) => {
    if (!user?.school_id) {
      toast.error('Please set up your school profile first');
      navigate('/settings');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData, is_draft: isDraft };
      if (id) {
        await lessonPlanAPI.update(id, payload);
        toast.success('Lesson plan updated');
      } else {
        const { data } = await lessonPlanAPI.create(payload);
        toast.success('Lesson plan created');
        navigate(`/builder/${data.lesson_plan_id}`, { replace: true });
      }
    } catch (error) {
      toast.error('Failed to save lesson plan');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (format, orientation = 'landscape', authorities = []) => {
    if (!id) {
      toast.error('Please save the lesson plan first');
      return;
    }

    try {
      const { data } = format === 'docx' 
        ? await documentAPI.generateDOCX(id, orientation, authorities)
        : await documentAPI.generatePDF(id, orientation, authorities);
      
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lesson_plan_${formData.subject}_${formData.class_name}_${orientation}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} (${orientation}) downloaded successfully`);
    } catch (error) {
      toast.error(`Failed to generate ${format.toUpperCase()}`);
    }
  };

  const openHistory = async () => {
    if (!id) {
      toast.error('Save the plan first to see history');
      return;
    }
    setShowHistoryModal(true);
    setLoadingVersions(true);
    try {
      const { data } = await lessonPlanAPI.listVersions(id);
      setVersions(data.versions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load history');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRestore = async (versionNumber) => {
    if (!id) return;
    try {
      const { data } = await lessonPlanAPI.restoreVersion(id, versionNumber);
      setFormData(data);
      setShowHistoryModal(false);
      toast.success(`Restored version ${versionNumber}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore version');
    }
  };

  const openShare = async () => {
    if (!id) {
      toast.error('Save the plan first to share');
      return;
    }
    setShowShareModal(true);
    setCreatingShareLink(true);
    try {
      const { data } = await lessonPlanAPI.createShareLink(id);
      const url = `${window.location.origin}/shared/${data.share_token}`;
      setShareUrl(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create share link');
    } finally {
      setCreatingShareLink(false);
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard');
  };

  const openVoiceModal = () => {
    setVoiceTranscript('');
    setVoiceIntent(null);
    setVoiceState('idle');
    setShowVoiceModal(true);
  };

  const startVoiceRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone not supported in this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '');
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        setVoiceState('processing');
        try {
          const file = new File([blob], 'voice.webm', { type: blob.type });
          const { data } = await voiceAPI.compose(file, formData.language || 'auto');
          setVoiceTranscript(data.transcript || '');
          setVoiceIntent(data.intent || {});
          setVoiceState('done');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Voice processing failed');
          setVoiceState('idle');
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setVoiceState('recording');
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const stopVoiceRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
  };

  const applyVoiceIntent = () => {
    if (!voiceIntent) return;
    const patch = {};
    if (voiceIntent.subject) patch.subject = voiceIntent.subject;
    if (voiceIntent.class_name) patch.class_name = voiceIntent.class_name;
    if (voiceIntent.chapter) patch.chapter = voiceIntent.chapter;
    if (voiceIntent.board) patch.board = voiceIntent.board;
    if (voiceIntent.book_name) patch.book_name = voiceIntent.book_name;
    if (voiceIntent.language) patch.language = voiceIntent.language;
    if (voiceIntent.num_days) patch.num_days = voiceIntent.num_days;
    if (voiceIntent.date_range) patch.date_range = voiceIntent.date_range;
    setFormData((prev) => ({ ...prev, ...patch }));
    setShowVoiceModal(false);
    toast.success('Form auto-filled from voice — review and click AI Generate');
  };

  const addDay = () => {
    setFormData({
      ...formData,
      days: [...formData.days, { 
        day: `DAY ${formData.days.length + 1}`, 
        outcomes: '', 
        curriculum_goals: '',
        teacher_activity: '', 
        student_activity: '', 
        skills: '', 
        innovative_practices: '',
        exit_card: '',
        remarks: '' 
      }],
    });
  };

  const removeDay = (index) => {
    if (formData.days.length === 1) {
      toast.error('At least one day is required');
      return;
    }
    setFormData({
      ...formData,
      days: formData.days.filter((_, i) => i !== index),
    });
  };

  const updateDay = (index, field, value) => {
    const updatedDays = [...formData.days];
    updatedDays[index] = { ...updatedDays[index], [field]: value };
    setFormData({ ...formData, days: updatedDays });
  };

  const handleAISuggestions = async (dayIndex) => {
    if (!formData.subject || !formData.class_name || !formData.chapter) {
      toast.error('Please fill in Subject, Class, and Chapter first');
      return;
    }

    setAiLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subject: formData.subject,
          class_name: formData.class_name,
          chapter: formData.chapter,
          day: formData.days[dayIndex].day,
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let suggestion = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            suggestion += line.substring(6);
          }
        }
      }

      updateDay(dayIndex, 'teacher_activity', suggestion);
      toast.success('AI suggestions added!');
    } catch (error) {
      console.error('AI suggestion error:', error);
      toast.error('Failed to get AI suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAutoGenerate = async () => {
    if (!formData.subject || !formData.class_name || !formData.chapter) {
      toast.error('Please fill in Subject, Class, and Topic first');
      return;
    }

    setAutoGenLoading(true);
    try {
      const hasContent = formData.days.some(d => d.outcomes || d.teacher_activity || d.student_activity);
      const parsedFromRange = parseDaysFromRange(formData.date_range);
      const numDays = Math.min(Math.max(
        hasContent ? formData.days.length : (formData.num_days || parsedFromRange || 5),
        1
      ), 15);

      const selectedTemplate = templates.find((tpl) => tpl.template_id === (formData.template_id || user?.default_template_id));
      const templateFields = selectedTemplate?.structure?.fields || null;
      const templateColumnLabels = selectedTemplate?.structure?.column_labels || null;

      const { data } = await aiAPI.autoGenerate({
        subject: formData.subject,
        class_name: formData.class_name,
        chapter: formData.chapter,
        board: formData.board || null,
        book_name: formData.book_name || null,
        num_days: numDays,
        include_references: true,
        language: formData.language || 'en',
        template_fields: templateFields,
        template_column_labels: templateColumnLabels,
        custom_prompt: (formData.custom_prompt || '').trim() || null,
        provider: aiProvider,
        model: aiProvider === 'anthropic' ? 'claude-haiku-4-5-20251001' : null,
      });

      setFormData({
        ...formData,
        days: data.days || formData.days,
        integration: data.integration || '',
        interdisciplinary: data.interdisciplinary || '',
        innovative: data.innovative || '',
        assessments: data.assessments || '',
      });

      if (data.web_references && data.web_references.length > 0) {
        setWebReferences(data.web_references);
      }

      toast.success('Lesson plan auto-generated! Review and edit as needed.');
    } catch (error) {
      console.error('Auto-generate error:', error);
      toast.error(error.response?.data?.detail || 'Failed to auto-generate plan');
    } finally {
      setAutoGenLoading(false);
    }
  };

  const parseDaysFromRange = (rangeStr) => {
    if (!rangeStr) return null;
    const dates = rangeStr.match(/\d{4}-\d{2}-\d{2}|\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4}/g);
    if (dates && dates.length >= 2) {
      const d1 = new Date(dates[0]);
      const d2 = new Date(dates[1]);
      if (!isNaN(d1.getTime()) && !isNaN(d2.getTime())) {
        const diff = Math.abs(Math.round((d2 - d1) / 86400000)) + 1;
        return Math.min(Math.max(diff, 1), 15);
      }
    }
    const singleNum = rangeStr.match(/(\d+)\s*(?:day|days|d)\b/i);
    if (singleNum) return Math.min(Math.max(parseInt(singleNum[1]), 1), 15);
    const rangeMatch = rangeStr.match(/(\d+)\s*[-–—to]+\s*(\d+)/);
    if (rangeMatch) {
      const n = Math.abs(parseInt(rangeMatch[2]) - parseInt(rangeMatch[1])) + 1;
      return Math.min(Math.max(n, 1), 15);
    }
    return null;
  };

  useEffect(() => {
    if (id) return;
    const numDays = parseDaysFromRange(formData.date_range);
    if (!numDays || numDays === formData.days.length) return;
    const hasContent = formData.days.some(d => d.outcomes || d.teacher_activity);
    if (hasContent) return;
    
    const newDays = Array.from({ length: numDays }, (_, i) => 
      formData.days[i] || { day: `DAY ${i + 1}`, outcomes: '', curriculum_goals: '', teacher_activity: '', student_activity: '', skills: '', innovative_practices: '', exit_card: '', remarks: '' }
    );
    setFormData(prev => ({ ...prev, days: newDays }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.date_range]);

  useEffect(() => {
    if (id) return;
    const parsed = parseDaysFromRange(formData.date_range);
    const numDays = parsed || Math.min(Math.max(parseInt(formData.num_days, 10) || 1, 1), 15);
    if (!numDays || numDays === formData.days.length) return;
    
    const hasContent = formData.days.some(d => d.outcomes || d.teacher_activity);
    if (hasContent) return;
    
    const newDays = Array.from({ length: numDays }, (_, i) => 
      formData.days[i] || { day: `DAY ${i + 1}`, outcomes: '', curriculum_goals: '', teacher_activity: '', student_activity: '', skills: '', innovative_practices: '', exit_card: '', remarks: '' }
    );
    setFormData(prev => ({ ...prev, days: newDays }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.date_range, formData.num_days]);

  useEffect(() => {
    if (id || autoTriggered) return;
    const allBasicFilled =
      formData.subject &&
      formData.class_name &&
      formData.chapter &&
      (formData.date_range || formData.num_days);
    if (!allBasicFilled) return;
    
    const hasContent = formData.days.some(d => d.outcomes || d.teacher_activity || d.student_activity);
    if (hasContent) return;

    if (autoTriggerTimer.current) clearTimeout(autoTriggerTimer.current);
    autoTriggerTimer.current = setTimeout(() => {
      setAutoTriggered(true);
      handleAutoGenerate();
    }, 1500);

    return () => {
      if (autoTriggerTimer.current) clearTimeout(autoTriggerTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.subject, formData.class_name, formData.chapter, formData.date_range, formData.num_days]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C53030] mx-auto mb-4"></div>
          <p className="text-stone-600">Loading lesson plan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button data-testid="home-button" variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#C53030]">
              <Home className="w-5 h-5 mr-2" /> Home
            </Button>
            <Button data-testid="back-button" variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-[#1C1917]">{id ? 'Edit' : 'Create'} Lesson Plan</h1>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="voice-compose-button"
              variant="outline"
              onClick={openVoiceModal}
              className="border border-stone-200"
              title="Voice Compose — dictate your lesson"
            >
              <Mic className="w-4 h-4 mr-2 text-[#C53030]" /> Voice
            </Button>
            <Button
              data-testid="auto-generate-button"
              onClick={handleAutoGenerate}
              disabled={autoGenLoading}
              className="bg-gradient-to-r from-[#C53030] to-[#9B2C2C] hover:from-[#9B2C2C] hover:to-[#7A1F1F] text-white rounded-md shadow-md"
            >
              <Wand2 className={`w-4 h-4 mr-2 ${autoGenLoading ? 'animate-spin' : ''}`} />
              {autoGenLoading ? 'Generating with AI...' : 'AI Generate'}
            </Button>
            <Select value={aiProvider} onValueChange={setAiProvider}>
              <SelectTrigger data-testid="lp-ai-provider" className="w-32 h-9 border border-stone-200 bg-white ml-2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="anthropic">Claude</SelectItem>
              </SelectContent>
            </Select>
            <Button
              data-testid="save-draft-button"
              variant="outline"
              onClick={() => handleSave(true)}
              disabled={saving}
              className="border border-stone-200"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            {id && (
              <>
                <Button
                  data-testid="history-button"
                  variant="outline"
                  onClick={openHistory}
                  className="border border-stone-200"
                >
                  <History className="w-4 h-4 mr-2" /> History
                </Button>
                <Button
                  data-testid="share-button"
                  variant="outline"
                  onClick={openShare}
                  className="border border-stone-200"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
              </>
            )}
            <Button
              data-testid="finalize-button"
              onClick={() => {
                if (!id) {
                  handleSave(false);
                } else {
                  setShowExportModal(true);
                }
              }}
              disabled={saving}
              className="bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md"
            >
              Finalize & Export
            </Button>
          </div>
        </div>
      </header>

      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent data-testid="export-modal" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Finalize & Export Lesson Plan</DialogTitle>
            <DialogDescription>
              Choose format, orientation, and signing authorities before download.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <Label className="text-sm font-semibold mb-3 block">Format</Label>
              <RadioGroup value={exportFormat} onValueChange={setExportFormat} className="grid grid-cols-2 gap-3">
                <label htmlFor="fmt-pdf" data-testid="format-pdf" className={`flex items-center gap-2 border rounded-md p-3 cursor-pointer ${exportFormat === 'pdf' ? 'border-[#C53030] bg-red-50' : 'border-stone-200'}`}>
                  <RadioGroupItem value="pdf" id="fmt-pdf" />
                  <span>PDF</span>
                </label>
                <label htmlFor="fmt-docx" data-testid="format-docx" className={`flex items-center gap-2 border rounded-md p-3 cursor-pointer ${exportFormat === 'docx' ? 'border-[#C53030] bg-red-50' : 'border-stone-200'}`}>
                  <RadioGroupItem value="docx" id="fmt-docx" />
                  <span>DOCX</span>
                </label>
              </RadioGroup>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-3 block">Page Orientation</Label>
              <RadioGroup value={orientation} onValueChange={setOrientation} className="grid grid-cols-2 gap-3">
                <label htmlFor="orient-portrait" data-testid="orientation-portrait" className={`flex flex-col items-center gap-2 border rounded-md p-4 cursor-pointer ${orientation === 'portrait' ? 'border-[#C53030] bg-red-50' : 'border-stone-200'}`}>
                  <RadioGroupItem value="portrait" id="orient-portrait" />
                  <div className="w-8 h-11 border-2 border-stone-400 rounded"></div>
                  <span className="text-sm">Portrait</span>
                </label>
                <label htmlFor="orient-landscape" data-testid="orientation-landscape" className={`flex flex-col items-center gap-2 border rounded-md p-4 cursor-pointer ${orientation === 'landscape' ? 'border-[#C53030] bg-red-50' : 'border-stone-200'}`}>
                  <RadioGroupItem value="landscape" id="orient-landscape" />
                  <div className="w-11 h-8 border-2 border-stone-400 rounded"></div>
                  <span className="text-sm">Landscape</span>
                </label>
              </RadioGroup>
              <p className="text-xs text-stone-500 mt-2">Landscape recommended for 7-column lesson plan tables.</p>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-3 block">Signing Authorities</Label>
              <p className="text-xs text-stone-500 mb-3">Select who should sign this lesson plan. Signature blocks will be added at the bottom.</p>
              <div className="grid grid-cols-2 gap-2">
                {SIGNING_AUTHORITIES.map((auth) => {
                  const checked = selectedAuthorities.includes(auth);
                  return (
                    <label
                      key={auth}
                      htmlFor={`auth-${auth}`}
                      data-testid={`authority-${auth.toLowerCase().replace(/ /g, '-')}`}
                      className={`flex items-center gap-2 border rounded-md p-2.5 cursor-pointer text-sm ${checked ? 'border-[#C53030] bg-red-50' : 'border-stone-200'}`}
                    >
                      <Checkbox
                        id={`auth-${auth}`}
                        checked={checked}
                        onCheckedChange={(v) => {
                          setSelectedAuthorities(v ? [...selectedAuthorities, auth] : selectedAuthorities.filter((a) => a !== auth));
                        }}
                      />
                      <span>{auth}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="cancel-export-button"
              variant="outline"
              onClick={() => setShowExportModal(false)}
              className="border border-stone-200"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-export-button"
              onClick={async () => {
                await handleSave(false);
                await handleExport(exportFormat, orientation, selectedAuthorities);
                setShowExportModal(false);
              }}
              className="bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent data-testid="history-modal" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>Last 10 versions. Restore any earlier version.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {loadingVersions ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#C53030] mx-auto" />
              </div>
            ) : versions.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-6">No prior versions yet. Snapshots start after your next edit.</p>
            ) : (
              versions.map((v) => (
                <div key={v.version_number} data-testid={`version-row-${v.version_number}`} className="flex items-center justify-between border border-stone-200 rounded-md p-3">
                  <div>
                    <p className="text-sm font-semibold text-[#1C1917]">v{v.version_number}{v.restore_note ? ` — ${v.restore_note}` : ''}</p>
                    <p className="text-xs text-stone-500">{new Date(v.created_at).toLocaleString()} • {v.editor_name || 'You'}</p>
                  </div>
                  <Button
                    data-testid={`restore-btn-${v.version_number}`}
                    size="sm"
                    variant="outline"
                    onClick={() => handleRestore(v.version_number)}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent data-testid="share-modal" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Share Lesson Plan</DialogTitle>
            <DialogDescription>Anyone with this link can view the plan (read-only). Author details are hidden.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {creatingShareLink ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#C53030] mx-auto" />
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <Input data-testid="share-link-input" readOnly value={shareUrl} />
                  <Button data-testid="copy-share-link-btn" onClick={copyShareLink} className="bg-[#C53030] hover:bg-[#9B2C2C] text-white">
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                </div>
                <p className="text-xs text-stone-500">Viewers can duplicate this plan into their own account after logging in.</p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showVoiceModal} onOpenChange={(o) => { setShowVoiceModal(o); if (!o) stopVoiceRecording(); }}>
        <DialogContent data-testid="voice-modal" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Voice Compose</DialogTitle>
            <DialogDescription>
              Tell us what to plan — e.g. "Create a 5-day lesson plan on Photosynthesis for Class 8 Science, NCERT".
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex flex-col items-center gap-3">
              {voiceState === 'idle' && (
                <Button
                  data-testid="voice-start-btn"
                  onClick={startVoiceRecording}
                  className="w-24 h-24 rounded-full bg-[#C53030] hover:bg-[#9B2C2C] text-white shadow-lg"
                >
                  <Mic className="w-10 h-10" />
                </Button>
              )}
              {voiceState === 'recording' && (
                <>
                  <Button
                    data-testid="voice-stop-btn"
                    onClick={stopVoiceRecording}
                    className="w-24 h-24 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg animate-pulse"
                  >
                    <Square className="w-10 h-10" fill="white" />
                  </Button>
                  <p className="text-sm text-red-600 font-semibold" data-testid="voice-recording-indicator">● Recording… tap to stop</p>
                </>
              )}
              {voiceState === 'processing' && (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-10 h-10 animate-spin text-[#C53030]" />
                  <p className="text-sm text-stone-600">Transcribing and extracting fields…</p>
                </div>
              )}
              {voiceState === 'done' && (
                <div className="w-full space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-stone-500">Transcript</Label>
                    <p data-testid="voice-transcript" className="text-sm text-stone-800 bg-stone-50 border border-stone-200 rounded p-3 mt-1">
                      {voiceTranscript || '(empty)'}
                    </p>
                  </div>
                  {voiceIntent && (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-stone-500">Detected Fields</Label>
                      <div data-testid="voice-intent" className="text-xs bg-emerald-50 border border-emerald-200 rounded p-3 mt-1 space-y-1">
                        {['subject', 'class_name', 'chapter', 'board', 'book_name', 'language', 'num_days', 'date_range'].map((k) => (
                          voiceIntent[k] ? <div key={k}><strong className="text-emerald-800">{k}:</strong> {String(voiceIntent[k])}</div> : null
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            {voiceState === 'done' && (
              <>
                <Button data-testid="voice-retry-btn" variant="outline" onClick={() => { setVoiceState('idle'); setVoiceTranscript(''); setVoiceIntent(null); }} className="border border-stone-200">
                  Retry
                </Button>
                <Button data-testid="voice-apply-btn" onClick={applyVoiceIntent} className="bg-[#C53030] hover:bg-[#9B2C2C] text-white">
                  Fill Form
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <CurriculumPicker onChange={setLibScope} defaultBoard={formData.board || 'CBSE'} defaultClass={formData.class_name} defaultSubject={formData.subject} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card className="p-6 border border-stone-200">
              <h2 className="text-xl sm:text-2xl text-[#1C1917] mb-2 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-md"><Sparkles className="w-5 h-5" /></span>
                Basic Information
              </h2>
              <p className="text-sm text-stone-600 mb-4">Set the context and let AI craft a beautiful, NCFSE-aligned plan tuned to your prompt.</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="custom_school_name">School Name (for this lesson plan)</Label>
                  <Input
                    id="custom_school_name"
                    data-testid="custom-school-name-input"
                    value={formData.custom_school_name || ''}
                    onChange={(e) => setFormData({ ...formData, custom_school_name: e.target.value })}
                    placeholder="Enter school name (leave blank to use school profile default)"
                    className="mt-1"
                  />
                  <p className="text-xs text-stone-500 mt-1">This overrides the default school name for the exported document only.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      data-testid="subject-input"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Mathematics"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="class_name">Class</Label>
                    <Input
                      id="class_name"
                      data-testid="class-input"
                      value={formData.class_name}
                      onChange={(e) => setFormData({ ...formData, class_name: e.target.value })}
                      placeholder="Class 8A"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="chapter">{t('topic')}</Label>
                  <Input
                    id="chapter"
                    data-testid="chapter-input"
                    value={formData.chapter}
                    onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                    placeholder="Pick a chapter from the Library above, or type your own"
                    className="mt-1"
                    autoComplete="off"
                  />
                  {libScope.chapter_ids?.length > 0 && (
                    <p className="text-xs text-emerald-700 mt-1" data-testid="curriculum-hint">
                      📚 {libScope.chapter_ids.length} chapter(s) selected from Curriculum Library
                    </p>
                  )}
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-orange-500" /> Custom Prompt (optional) — tell AI exactly how you want this plan
                  </Label>
                  <Textarea
                    data-testid="custom-prompt-input"
                    value={formData.custom_prompt || ''}
                    onChange={(e) => setFormData({ ...formData, custom_prompt: e.target.value })}
                    placeholder="e.g. Focus on Vedic math tricks; add 2 real-life word problems per day; use inquiry-based learning; keep the tone conversational."
                    rows={3}
                    className="mt-1 border-orange-200 focus:border-orange-400 focus:ring-orange-300"
                    maxLength={2000}
                  />
                  <p className="text-xs text-orange-700 mt-1">🎨 Personalise this lesson plan — style, focus areas, activities, tone, examples.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('board')}</Label>
                    <Select
                      value={formData.board || ''}
                      onValueChange={(v) => setFormData({ ...formData, board: v })}
                    >
                      <SelectTrigger data-testid="board-select" className="mt-1">
                        <SelectValue placeholder={t('selectBoard')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NCERT">NCERT</SelectItem>
                        <SelectItem value="CBSE">CBSE</SelectItem>
                        <SelectItem value="ICSE">ICSE</SelectItem>
                        <SelectItem value="IB">IB (International Baccalaureate)</SelectItem>
                        <SelectItem value="Cambridge">Cambridge (IGCSE)</SelectItem>
                        <SelectItem value="State Board">State Board</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="book_name">{t('bookName')}</Label>
                    <Input
                      id="book_name"
                      data-testid="book-name-input"
                      value={formData.book_name || ''}
                      onChange={(e) => setFormData({ ...formData, book_name: e.target.value })}
                      placeholder="e.g., Mathematics for Class 8"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('lessonFormat')}</Label>
                  <Select
                    value={formData.template_id || 'default'}
                    onValueChange={(v) => setFormData({ ...formData, template_id: v === 'default' ? '' : v })}
                  >
                    <SelectTrigger data-testid="template-select" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">{t('useProfileDefault')}</SelectItem>
                      {templates.map((tpl) => (
                        <SelectItem key={tpl.template_id} value={tpl.template_id}>{tpl.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('planLanguage')}</Label>
                  <Select
                    value={formData.language || 'en'}
                    onValueChange={(v) => setFormData({ ...formData, language: v })}
                  >
                    <SelectTrigger data-testid="plan-language-select" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English (default)</SelectItem>
                      <SelectItem value="hi">हिन्दी (Hindi)</SelectItem>
                      <SelectItem value="sa">संस्कृतम् (Sanskrit)</SelectItem>
                      <SelectItem value="fr">Français (French)</SelectItem>
                      <SelectItem value="es">Español (Spanish)</SelectItem>
                      <SelectItem value="zh">中文 (Mandarin)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-stone-500 mt-1">Content will be generated in this language.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="teacher_name">{t('teacherName')}</Label>
                    <Input
                      id="teacher_name"
                      data-testid="teacher-name-input"
                      value={formData.teacher_name}
                      onChange={(e) => setFormData({ ...formData, teacher_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="date_range">{t('dateRange')}</Label>
                    <Input
                      id="date_range"
                      data-testid="date-range-input"
                      value={formData.date_range}
                      onChange={(e) => setFormData({ ...formData, date_range: e.target.value })}
                      placeholder="Jan 15-20, 2026"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="num_days">{t('numberOfDays')}</Label>
                  <Input
                    id="num_days"
                    data-testid="num-days-input"
                    type="number"
                    min={1}
                    max={15}
                    value={formData.num_days || ''}
                    onChange={(e) => setFormData({ ...formData, num_days: parseInt(e.target.value, 10) || '' })}
                    placeholder="5"
                    className="mt-1"
                  />
                  <p className="text-xs text-stone-500 mt-1">{t('daysHint')}</p>
                </div>
              </div>
            </Card>

            {formData.days.map((day, index) => (
              <Card key={`day-${day.day}-${index}`} data-testid={`day-card-${index}`} className="p-6 border border-stone-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#1C1917]">{day.day}</h3>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`ai-suggest-button-${index}`}
                      size="sm"
                      onClick={() => handleAISuggestions(index)}
                      disabled={aiLoading}
                      className="bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md"
                    >
                      <Sparkles className={`w-4 h-4 mr-1 ${aiLoading ? 'animate-pulse' : ''}`} />
                      {aiLoading ? 'Loading...' : 'AI Suggest'}
                    </Button>
                    {formData.days.length > 1 && (
                      <Button
                        data-testid={`remove-day-button-${index}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => removeDay(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Label>Learning Outcomes</Label>
                      <span className="text-[10px] uppercase tracking-wider text-[#C53030] bg-red-50 px-1.5 py-0.5 rounded font-medium">AI-filled</span>
                    </div>
                    <Textarea
                      data-testid={`outcomes-input-${index}`}
                      value={day.outcomes}
                      onChange={(e) => updateDay(index, 'outcomes', e.target.value)}
                      rows={3}
                      placeholder="Automatically filled when you click AI Generate — you can edit"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Label>Curriculum Goals</Label>
                      <span className="text-[10px] uppercase tracking-wider text-[#C53030] bg-red-50 px-1.5 py-0.5 rounded font-medium">AI-filled</span>
                    </div>
                    <Textarea
                      data-testid={`curriculum-goals-input-${index}`}
                      value={day.curriculum_goals}
                      onChange={(e) => updateDay(index, 'curriculum_goals', e.target.value)}
                      rows={3}
                      placeholder="Automatically filled with curriculum-aligned goals — you can edit"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Teacher Activity</Label>
                    <Textarea
                      data-testid={`teacher-activity-input-${index}`}
                      value={day.teacher_activity}
                      onChange={(e) => updateDay(index, 'teacher_activity', e.target.value)}
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Student Activity</Label>
                    <Textarea
                      data-testid={`student-activity-input-${index}`}
                      value={day.student_activity}
                      onChange={(e) => updateDay(index, 'student_activity', e.target.value)}
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Skills</Label>
                    <Input
                      data-testid={`skills-input-${index}`}
                      value={day.skills}
                      onChange={(e) => updateDay(index, 'skills', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Label>Innovative Practices</Label>
                      <span className="text-[10px] uppercase tracking-wider text-[#C53030] bg-red-50 px-1.5 py-0.5 rounded font-medium">AI-filled</span>
                    </div>
                    <Textarea
                      data-testid={`innovative-practices-input-${index}`}
                      value={day.innovative_practices}
                      onChange={(e) => updateDay(index, 'innovative_practices', e.target.value)}
                      rows={2}
                      placeholder="Auto-filled with concrete innovative teaching practices for this day"
                      className="mt-1"
                    />
                  </div>
                  <div className="border-l-4 border-[#C53030] bg-amber-50 rounded-md p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Label className="text-[#C53030] font-semibold">Exit Card (Quick Assessment)</Label>
                      <span className="text-[10px] uppercase tracking-wider text-[#C53030] bg-red-100 px-1.5 py-0.5 rounded font-medium">AI-filled</span>
                    </div>
                    <p className="text-xs text-stone-600 mb-2">3-5 min end-of-lesson check-in — separate from daily activities</p>
                    <Textarea
                      data-testid={`exit-card-input-${index}`}
                      value={day.exit_card}
                      onChange={(e) => updateDay(index, 'exit_card', e.target.value)}
                      rows={3}
                      placeholder="Auto-filled: 3-question quick check, exit ticket, or one-minute paper"
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label>Remarks</Label>
                    <Input
                      data-testid={`remarks-input-${index}`}
                      value={day.remarks}
                      onChange={(e) => updateDay(index, 'remarks', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              </Card>
            ))}

            <Button
              data-testid="add-day-button"
              onClick={addDay}
              variant="outline"
              className="w-full border border-stone-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Day
            </Button>

            <Card className="p-6 border border-stone-200">
              <h2 className="text-xl sm:text-2xl text-[#1C1917] mb-4">Additional Details</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="integration">Integration</Label>
                  <Textarea
                    id="integration"
                    data-testid="integration-input"
                    value={formData.integration}
                    onChange={(e) => setFormData({ ...formData, integration: e.target.value })}
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="assessments">Assessment Methods</Label>
                  <Textarea
                    id="assessments"
                    data-testid="assessments-input"
                    value={formData.assessments}
                    onChange={(e) => setFormData({ ...formData, assessments: e.target.value })}
                    rows={2}
                    className="mt-1"
                  />
                </div>
              </div>
            </Card>

            {webReferences.length > 0 && (
              <Card className="p-6 border border-stone-200">
                <h2 className="text-xl sm:text-2xl text-[#1C1917] mb-4 flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-[#C53030]" />
                  Recommended Resources
                </h2>
                <div className="space-y-3">
                  {webReferences.map((ref, idx) => (
                    <a
                      key={`webref-${ref.url || idx}`}
                      href={ref.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`web-reference-${idx}`}
                      className="block p-3 border border-stone-200 rounded-md hover:bg-stone-50 hover:border-[#C53030] transition-colors"
                    >
                      <div className="font-semibold text-[#C53030] text-sm mb-1">{ref.title}</div>
                      <div className="text-xs text-stone-600">{ref.description}</div>
                      <div className="text-xs text-stone-400 mt-1 truncate">{ref.url}</div>
                    </a>
                  ))}
                </div>
              </Card>
            )}
          </div>

          <div className="lg:sticky lg:top-24 h-fit">
            <Card className="p-6 border border-stone-200">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl sm:text-2xl text-[#1C1917]">Preview</h2>
                {id && (
                  <div className="flex gap-2">
                    <Button
                      data-testid="export-docx-button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleExport('docx')}
                      className="border border-stone-200"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      DOCX
                    </Button>
                    <Button
                      data-testid="export-pdf-button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleExport('pdf')}
                      className="border border-stone-200"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                )}
              </div>
              <div className="bg-white border border-stone-200 p-6 rounded-md shadow-sm max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="text-center mb-6">
                  {school?.logo_url && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${school.logo_url}`}
                      alt="School logo"
                      className="h-14 mx-auto mb-2 object-contain"
                    />
                  )}
                  <h1 className="text-2xl font-bold text-[#C53030] mb-1">
                    {formData.custom_school_name?.trim() || school?.name || 'School Name'}
                  </h1>
                  {school?.tagline && <p className="text-sm text-stone-600">{school.tagline}</p>}
                </div>
                <div className="space-y-2 text-sm mb-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div><strong>Subject:</strong> {formData.subject || '-'}</div>
                    <div><strong>Class:</strong> {formData.class_name || '-'}</div>
                    <div><strong>Date:</strong> {formData.date_range || '-'}</div>
                    <div><strong>Chapter:</strong> {formData.chapter || '-'}</div>
                  </div>
                  <div><strong>Teacher:</strong> {formData.teacher_name || '-'}</div>
                </div>
                <div className="border-t border-stone-200 pt-4">
                  {formData.days.map((day, index) => (
                    <div key={`preview-${day.day}-${index}`} className="mb-4 pb-4 border-b border-stone-200 last:border-0">
                      <h3 className="font-semibold text-[#1C1917] mb-2">{day.day}</h3>
                      <div className="space-y-1 text-xs">
                        {day.outcomes && <p><strong>Outcomes:</strong> {day.outcomes}</p>}
                        {day.curriculum_goals && <p><strong>Curriculum Goals:</strong> {day.curriculum_goals}</p>}
                        {day.teacher_activity && <p><strong>Teacher:</strong> {day.teacher_activity}</p>}
                        {day.student_activity && <p><strong>Student:</strong> {day.student_activity}</p>}
                        {day.skills && <p><strong>Skills:</strong> {day.skills}</p>}
                        {day.innovative_practices && <p><strong>Innovative:</strong> {day.innovative_practices}</p>}
                        {day.exit_card && (
                          <div className="mt-2 p-2 border-l-2 border-[#C53030] bg-amber-50 rounded">
                            <strong className="text-[#C53030]">Exit Card:</strong> {day.exit_card}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LessonPlanBuilder;